import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {ContactUsBanner} from '@/components/common/banner/ContactUsBanner'
import {ContentLayoutWithSidebar} from '@/components/common/content/ContentLayoutWithSidebar'
import {Section} from '@/components/common/content/Section'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {HeaderMainButtonProps} from '@/components/header/HeaderMainButton'
import {Page} from '@/components/Page'
import {getQuestionsAndAnswersContent} from '@/content/questionsAndAnswers'
import {useTranslation} from '@/i18n'
import {RecentArticleCard} from '@/utils/contentful'
import {QuestionsAndAnswersContentType} from '@/utils/questions-and-answers/constants'
import {
  QuestionsAndAnswersCategory,
  QuestionsAndAnswersCategoryItem,
} from '@/utils/questions-and-answers/types'

import {QuestionsAndAnswersCategoryContent} from '../QuestionsAndAnswersCategoryContent'
import {QuestionsAndAnswersResources} from '../QuestionsAndAnswersResources'

type QuestionsAndAnswersCategoryPageProps = {
  // Built in the route, so one array drives the visible trail and the BreadcrumbList.
  breadcrumbTrail: BreadcrumbStep[]
  type: QuestionsAndAnswersContentType
  category: QuestionsAndAnswersCategory
  categoryItems: QuestionsAndAnswersCategoryItem[]
  relatedArticles: RecentArticleCard[]
  mainCtaButton?: HeaderMainButtonProps
}

export const QuestionsAndAnswersCategoryPage: FC<QuestionsAndAnswersCategoryPageProps> = ({
  breadcrumbTrail,
  type,
  category,
  categoryItems,
  relatedArticles,
  mainCtaButton,
}) => {
  const {t} = useTranslation()
  const {relatedArticlesTitle, helpCenterCategories} = getQuestionsAndAnswersContent(type)
  const {name, slug, intro, relatedServices} = category

  return (
    <Page helpCenterCategories={helpCenterCategories} mainCtaButton={mainCtaButton}>
      <Section hasPaddingTop>
        <ContentLayoutWithSidebar
          breadcrumbTrail={breadcrumbTrail}
          layoutTitle={name}
          relatedServices={relatedServices}
        >
          <QuestionsAndAnswersCategoryContent
            type={type}
            categorySlug={slug}
            description={intro}
            items={categoryItems}
          />
        </ContentLayoutWithSidebar>
      </Section>
      {relatedArticles.length > 0 && (
        <Section hasPaddingTop>
          <MostRecentArticles
            articlesTitle={t(relatedArticlesTitle)}
            articles={relatedArticles}
            trackedEvent={{
              name: 'RelatedBlogs-QA',
              parameters: {type, category: name},
            }}
          />
        </Section>
      )}
      <Section hasPaddingTop>
        <QuestionsAndAnswersResources type={type} />
      </Section>
      <Section hasPaddingTop hasPaddingBottom>
        <ContactUsBanner
          trackedEventParameters={{
            position: 'Contact us banner - QA',
            type,
            category: name,
          }}
        />
      </Section>
    </Page>
  )
}
